@if (config) {
  <div fxLayout="column" fxFlex fxLayoutGap="24px">
    <!-- Header -->
    <div fxLayout="row" fxLayoutAlign="space-between center">
      <h2>Credential Configuration</h2>
      <div>
        <button mat-icon-button [routerLink]="['../']" matTooltip="Back to list">
          <mat-icon>arrow_back</mat-icon>
        </button>
        <button mat-icon-button [routerLink]="['edit']" matTooltip="Edit configuration">
          <mat-icon>edit</mat-icon>
        </button>
        <button mat-icon-button matTooltip="Backup configuration" (click)="downloadConfig()">
          <mat-icon>download</mat-icon>
        </button>
        <button
          mat-icon-button
          (click)="deleteConfig()"
          matTooltip="Delete configuration"
          color="warn"
        >
          <mat-icon>delete</mat-icon>
        </button>
      </div>
    </div>

    <!-- Basic Information Card -->
    <mat-card>
      <mat-card-header>
        <mat-card-title>
          <mat-icon>settings</mat-icon>
          Basic Information
        </mat-card-title>
      </mat-card-header>
      <mat-card-content fxLayout="column" fxLayoutGap="16px">
        <div fxLayout="row" fxLayoutGap="32px" fxLayout.lt-md="column">
          <div fxFlex>
            <div><strong>Configuration ID:</strong></div>
            <div class="value-text">{{ config.id }}</div>
          </div>
          @if (config.key) {
            <div fxFlex>
              <div><strong>Key</strong></div>
              <div class="value-text">
                <a [routerLink]="['/key-management', config.key.id]">
                  {{ config.key.id }} ({{ config.key.description }})
                </a>
              </div>
            </div>
          }
          <div fxFlex>
            <div><strong>Lifetime:</strong></div>
            <div class="value-text">{{ formatLifetime(config.lifeTime) }}</div>
          </div>
        </div>

        <!-- Feature Flags -->
        <div>
          <strong>Features:</strong>
          <div fxLayout="row" fxLayoutGap="8px" class="feature-chips">
            <mat-chip-set>
              <mat-chip [color]="config.keyBinding ? 'primary' : 'basic'">
                <mat-icon>{{ config.keyBinding ? 'lock' : 'lock_open' }}</mat-icon>
                Key Binding: {{ config.keyBinding ? 'Enabled' : 'Disabled' }}
              </mat-chip>
              <mat-chip [color]="config.statusManagement ? 'primary' : 'basic'">
                <mat-icon>{{ config.statusManagement ? 'verified' : 'block' }}</mat-icon>
                Status Management:
                {{ config.statusManagement ? 'Enabled' : 'Disabled' }}
              </mat-chip>
            </mat-chip-set>
          </div>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- VCT Information Card -->
    @if (config.vct) {
      <mat-card>
        <mat-card-header>
          <mat-card-title>
            <mat-icon>description</mat-icon>
            Verifiable Credential Type (VCT)
          </mat-card-title>
        </mat-card-header>
        <mat-card-content fxLayout="column" fxLayoutGap="16px">
          <div>
            <div><strong>Name:</strong></div>
            <div class="value-text">{{ config.vct.name || 'Not specified' }}</div>
          </div>

          @if (config.vct.description) {
            <div>
              <div><strong>Description:</strong></div>
              <div class="value-text">{{ config.vct.description }}</div>
            </div>
          }
          @if (config.vct.extends) {
            <div>
              <div><strong>Extends:</strong></div>
              <div class="value-text">{{ config.vct.extends }}</div>
            </div>
          }
          @if (config.vct.schema_uri) {
            <div>
              <div><strong>Schema URI:</strong></div>
              <div class="value-text">{{ config.vct.schema_uri }}</div>
            </div>
          }
        </mat-card-content>
      </mat-card>
    }

    <app-webhook-config-show
      [config]="config.claimsWebhook"
      title="Claims"
      descriptions="to fetch claims during issuance"
    ></app-webhook-config-show>

    <app-webhook-config-show
      [config]="config.notificationWebhook"
      title="Notifications"
      descriptions="to receive notifications during issuance"
    ></app-webhook-config-show>

    <!-- Expandable Sections -->
    <mat-accordion>
      <!-- Configuration Details -->
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon>tune</mat-icon>
            Configuration Details
          </mat-panel-title>
          <mat-panel-description> Technical configuration parameters </mat-panel-description>
        </mat-expansion-panel-header>

        @if (getObjectKeys(config.config).length > 0) {
          <div fxLayout="column" fxLayoutGap="12px">
            @for (key of getObjectKeys(config.config); track key) {
              <div class="config-item">
                <div class="config-key">{{ key }}:</div>
                <pre class="config-value">{{ formatJsonValue(asAny(config.config)[key]) }}</pre>
              </div>
            }
          </div>
        } @else {
          <div class="empty-state">
            <mat-icon>info</mat-icon>
            <span>No configuration parameters defined</span>
          </div>
        }
      </mat-expansion-panel>

      <!-- Default Claims -->
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon>fact_check</mat-icon>
            Default Claims
          </mat-panel-title>
          <mat-panel-description> Claims that will be included by default </mat-panel-description>
        </mat-expansion-panel-header>

        @if (getObjectKeys(config.claims).length > 0) {
          <div fxLayout="column" fxLayoutGap="12px">
            @for (key of getObjectKeys(config.claims); track key) {
              <div class="config-item">
                <div class="config-key">{{ key }}:</div>
                <pre class="config-value">{{ formatJsonValue(config.claims![key]) }}</pre>
              </div>
            }
          </div>
        } @else {
          <div class="empty-state">
            <mat-icon>info</mat-icon>
            <span>No default claims defined</span>
          </div>
        }
      </mat-expansion-panel>

      <!-- Disclosure Frame -->
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon>visibility</mat-icon>
            Disclosure Frame
          </mat-panel-title>
          <mat-panel-description> SD-JWT VC disclosure configuration </mat-panel-description>
        </mat-expansion-panel-header>

        @if (getObjectKeys(config.disclosureFrame).length > 0) {
          <div fxLayout="column" fxLayoutGap="12px">
            @for (key of getObjectKeys(config.disclosureFrame); track key) {
              <div class="config-item">
                <div class="config-key">{{ key }}:</div>
                <pre class="config-value">{{ formatJsonValue(config.disclosureFrame![key]) }}</pre>
              </div>
            }
          </div>
        } @else {
          <div class="empty-state">
            <mat-icon>info</mat-icon>
            <span>No disclosure frame defined</span>
          </div>
        }
      </mat-expansion-panel>

      <!-- JSON Schema -->
      @if (config.schema) {
        <mat-expansion-panel>
          <mat-expansion-panel-header>
            <mat-panel-title>
              <mat-icon>schema</mat-icon>
              JSON Schema
            </mat-panel-title>
            <mat-panel-description> Schema validation for claims </mat-panel-description>
          </mat-expansion-panel-header>

          <div fxLayout="column" fxLayoutGap="16px">
            <div class="config-item">
              <div class="config-key">Schema:</div>
              <pre class="config-value">{{ formatJsonValue(config.schema.$schema) }}</pre>
            </div>

            <div class="config-item">
              <div class="config-key">Type:</div>
              <pre class="config-value">{{ formatJsonValue(config.schema.type) }}</pre>
            </div>

            @if (config.schema.required && config.schema.required.length > 0) {
              <div class="config-item">
                <div class="config-key">Required Fields:</div>
                <div fxLayout="row wrap" fxLayoutGap="8px" class="required-fields">
                  @for (field of config.schema.required; track field) {
                    <mat-chip>{{ field }}</mat-chip>
                  }
                </div>
              </div>
            }

            <div class="config-item">
              <div class="config-key">Properties:</div>
              <pre class="config-value">{{ formatJsonValue(config.schema.properties) }}</pre>
            </div>
          </div>
        </mat-expansion-panel>
      }

      <!-- Embedded Disclosure Policy -->
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon>policy</mat-icon>
            Embedded Disclosure Policy
          </mat-panel-title>
          <mat-panel-description> Policy configuration for the credential </mat-panel-description>
        </mat-expansion-panel-header>

        <div fxLayout="column" fxLayoutGap="16px">
          <div class="config-item">
            <div class="config-key">Policy:</div>
            <pre class="config-value">{{ formatJsonValue(config.embeddedDisclosurePolicy) }}</pre>
          </div>
        </div>
      </mat-expansion-panel>
    </mat-accordion>
  </div>
}
